<template>
    <div
        class="module-wrapper"
        :class="{ 'options-header': hasHeader, 'card-main': !props.hideCardStyling }"
        @mouseenter="onWrapperEnter"
        @focusin="onWrapperEnter"
        @mouseleave="onWrapperLeave"
        @focusout="onWrapperLeave"
    >
        <conditional-teleport :to="moduleChartPopOut">
            <div v-if="slots.top" class="module-top">
                <slot name="top" />
            </div>
            <module-footer
                v-if="hasOptions"
                ref="editOptionsRef"
                :moduleName="props.title || ''"
                :blocked-actions="footerBlockedActions"
                :has-left-options="hasLeftSection"
                :display-options-top="showChartPopout || props.displayOptionsTop"
                :collision-padding-top="props.collisionPaddingTop"
                :is-modal="props.isModal || showChartPopout"
                :show-mobile-trigger="!hasHeader"
                @delete:module="deleteModule"
                @clone:module="cloneModule"
                @edit:module="editChartModule"
                @fullscreen:module="emit('fullscreen:module')"
                @pop-out:module="openChartPopout"
            >
                <template v-if="hasLeftSection" #left="{ updatePopoverVisibility, displayOptionsTop }">
                    <slot
                        name="left"
                        :updatePopoverVisibility="updatePopoverVisibility"
                        :displayOptionsTop="displayOptionsTop"
                    />
                </template>
            </module-footer>
            <module-header
                v-if="hasHeader"
                :title="props.title"
                :blocked-actions="headerBlockedActions"
                :showOptionsButton="hasOptions"
                @click:options="openModuleOptions"
            >
                <template #centre>
                    <slot name="centre" />
                </template>
                <template #right>
                    <slot name="right" :is-modal="props.isModal || showChartPopout" />
                </template>
            </module-header>
            <div class="content-cell cancel-drag">
                <slot name="content" />
            </div>
        </conditional-teleport>
        <central-modal
            :header="props.title ?? 'Expanded'"
            v-model:visible="showChartPopout"
            @hide="reflowCount += 1"
            size="medium"
        >
            <div
                ref="moduleChartPopOut"
                class="module-wrapper"
                :class="{ 'options-header': props.title || !props.hideDate }"
                @mouseenter="onWrapperEnter"
                @focusin="onWrapperEnter"
                @mouseleave="onWrapperLeave"
                @focusout="onWrapperLeave"
            />
        </central-modal>
    </div>
</template>

<script setup lang="ts">
import CentralModal from '@/components/common/CentralModal.vue';
import {
    computed,
    ref,
    useSlots,
} from 'vue';
import ModuleFooter from '@/components/common/ModuleFooter.vue';
import ModuleHeader from '@/components/powerMarkets/chart/ModuleHeader.vue';
import ConditionalTeleport from '@/components/layout/ConditionalTeleport.vue';
import { useUIStore } from '@/stores/uiStore.ts';
import { storeToRefs } from 'pinia';
import {
    CANNOT_POPOUT_BLOCKED_ACTIONS, IModuleBlockedActions, NO_ACCESS_BLOCKED_ACTIONS, NO_DATA_BLOCKED_ACTIONS, VIEW_ONLY_CHART_BLOCKED_ACTIONS,
} from '@/interfaces/actions/enact/IModuleActions.ts';

const props = defineProps<{
    title?: string;
    hideDate?: boolean;
    hideIcons?: boolean;
    blockedActions?: IModuleBlockedActions;
    noData?: boolean;
    noAccess?: boolean;
    hasLeftOptions?: boolean;
    isModal?: boolean;
    hideCardStyling?: boolean;
    customPopout?: boolean;
    showHeader?: boolean;
    displayOptionsTop?: boolean;
    collisionPaddingTop?: number;
}>();

const emit = defineEmits<{
    'delete:module': [];
    'edit:module': [];
    'clone:module': [Array<string>];
    'fullscreen:module': [];
    'pop-out:module': [];
}>();

const uiStore = useUIStore();
const { isMobileView } = storeToRefs(uiStore);

const showChartPopout = ref<boolean>(false);
const moduleChartPopOut = ref<HTMLElement | null>(null);

const editOptionsRef = ref<InstanceType<typeof ModuleFooter> | null>(null);

const reflowCount = ref<number>(0);

const slots = useSlots();
const headerBlockedActions = computed<IModuleBlockedActions | undefined>(() => {
    if (showChartPopout.value) return { ...props.blockedActions, changeLayout: true };
    return props.blockedActions;
});

const footerBlockedActions = computed<IModuleBlockedActions | undefined>(() => {
    if (props.noAccess) {
        return { ...props.blockedActions, ...NO_ACCESS_BLOCKED_ACTIONS };
    }

    if (props.noData) {
        return { ...props.blockedActions, ...NO_DATA_BLOCKED_ACTIONS };
    }

    if (showChartPopout.value) {
        return {
            ...props.blockedActions,
            ...CANNOT_POPOUT_BLOCKED_ACTIONS,
            ...VIEW_ONLY_CHART_BLOCKED_ACTIONS,
        };
    }

    return props.blockedActions;
});

const hasLeftSection = computed<boolean>(() => props.hasLeftOptions ?? !!slots.left);

const hasOptions = computed<boolean>(() => {
    if (props.hideIcons) return false;
    if (hasLeftSection.value) return true;

    const blocked = footerBlockedActions.value;
    return !blocked?.fullscreen
        || !blocked?.popout
        || !blocked?.clone
        || !blocked?.edit
        || !blocked?.delete;
});

const hasHeader = computed<boolean>(() => props.showHeader || !!props.title || !props.hideDate || !!slots.centre || !!slots.right);

function openModuleOptions (e: Event): void {
    editOptionsRef.value?.showSettings(e);
}

function openChartPopout (): void {
    if (props.customPopout) {
        emit('pop-out:module');
        return;
    }
    showChartPopout.value = true;
}

function editChartModule (): void {
    emit('edit:module');
}

function deleteModule (): void {
    emit('delete:module');
}

function cloneModule (dashboardIds: Array<string>): void {
    emit('clone:module', dashboardIds);
}

function onWrapperEnter (e: Event): void {
    if (isMobileView.value) return;
    editOptionsRef.value?.showSettings(e);
}

function onWrapperLeave (): void {
    if (isMobileView.value) return;
    editOptionsRef.value?.hideSettings();
}
</script>

<style lang="scss" scoped>
.module-wrapper {
    height: 100%;
    width: 100%;
    display: grid;
    overflow: hidden;
    position: relative;

    &:hover{
        :deep(.module-footer-wrapper) {
            transform: translateY(calc(-100% + 19px)); // hover icon height - 1px for borders
        }
        :deep(.date-button),
        :deep(.context-trigger) {
            .icon {
                display: block;
                opacity: 1;
                width: 2rem;
            }
        }
        :deep(.icon-controls) {
            opacity: 1;
        }
    }

    &.options-header{
        grid-template-rows: auto 1fr;
    }
}

:deep(.table-options){
    .p-button{
        .icon{
            color: var(--accentColour);
        }
    }
}

.content-cell {
    height: 100%;
    width: 100%;
    overflow: hidden;
    position: relative;
}

.module-top {
    position: absolute;
    inset: 0 0 auto;
    z-index: 1;
    pointer-events: none;
}

.module-top ~ :deep(.chart-module-header) {
    padding-top: var(--defaultSpacing);
}
</style>
